// src/Pages/ContractTracking.js
import React, { useState, useEffect } from 'react';
import { api } from '../api';

export default function ContractTracking() {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [contracts, setContracts] = useState([]);
  const [manualContracts, setManualContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [isCached, setIsCached] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showManualContractModal, setShowManualContractModal] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [activeTab, setActiveTab] = useState('partner'); // 'artist' or 'partner'
  const [partnerYearFilter, setPartnerYearFilter] = useState('ALL'); // Separate year filter for partner contracts
  const [formData, setFormData] = useState({
    partnerName: '',
    contractType: '',
    expiryDate: '',
    noticeMonths: 0,
    notes: '',
  });

  // Generate year options (current year + 10 years forward)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => currentYear + i);

  // Fetch spreadsheet contracts from backend
  const fetchContracts = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const url = forceRefresh 
        ? '/api/contract-tracking?refresh=true' 
        : '/api/contract-tracking';
      const res = await api(url);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch contracts');
      }
      const data = await res.json();
      setContracts(data.contracts || []);
      setIsCached(data.cached || false);
      setLastFetched(new Date());
    } catch (err) {
      console.error('Error fetching contracts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch manual contracts from backend
  const fetchManualContracts = async () => {
    try {
      const res = await api('/api/manual-contracts');
      if (!res.ok) {
        throw new Error('Failed to fetch manual contracts');
      }
      const data = await res.json();
      setManualContracts(data.contracts || []);
    } catch (err) {
      console.error('Error fetching manual contracts:', err);
    }
  };

  useEffect(() => {
    fetchContracts();
    fetchManualContracts();
  }, []);

  // Handle manual contract form submission
  const handleSaveManualContract = async () => {
    try {
      if (!formData.partnerName || !formData.expiryDate) {
        alert('Partner name and expiry date are required');
        return;
      }

      if (editingContract) {
        // Update existing contract
        const res = await api(`/api/manual-contracts/${editingContract._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!res.ok) throw new Error('Failed to update contract');
      } else {
        // Create new contract
        const res = await api('/api/manual-contracts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!res.ok) throw new Error('Failed to create contract');
      }

      // Refresh manual contracts and close modal
      await fetchManualContracts();
      setShowManualContractModal(false);
      setEditingContract(null);
      setFormData({ partnerName: '', contractType: '', expiryDate: '', noticeMonths: 0, notes: '' });
    } catch (err) {
      console.error('Error saving manual contract:', err);
      alert(err.message);
    }
  };

  // Handle delete manual contract
  const handleDeleteManualContract = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contract?')) {
      return;
    }
    try {
      const res = await api(`/api/manual-contracts/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete contract');
      await fetchManualContracts();
    } catch (err) {
      console.error('Error deleting manual contract:', err);
      alert(err.message);
    }
  };

  // Open modal for editing
  const handleEditManualContract = (contract) => {
    setEditingContract(contract);
    setFormData({
      partnerName: contract.partnerName,
      contractType: contract.contractType || '',
      expiryDate: new Date(contract.expiryDate).toISOString().split('T')[0],
      noticeMonths: contract.noticeMonths || 0,
      notes: contract.notes || '',
    });
    setShowManualContractModal(true);
  };

  // Open modal for creating new
  const handleAddManualContract = () => {
    setEditingContract(null);
    setFormData({ partnerName: '', contractType: '', expiryDate: '', noticeMonths: 0, notes: '' });
    setShowManualContractModal(true);
  };

  // Filter spreadsheet contracts by search query or year
  const filteredContracts = contracts.filter((contract) => {
    // If there's a search query, search ALL contracts (ignore year filter)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesArtist = contract.artistName?.toLowerCase().includes(query);
      const matchesRelease = contract.releaseName?.toLowerCase().includes(query);
      return matchesArtist || matchesRelease;
    }
    
    // If no search query, filter by selected year
    const expiryYear = new Date(contract.expiryDate).getFullYear();
    return expiryYear === selectedYear;
  });

  // Filter manual contracts by search query or year
  const filteredManualContracts = manualContracts.filter((contract) => {
    // If there's a search query, search ALL contracts (ignore year filter)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesPartner = contract.partnerName?.toLowerCase().includes(query);
      const matchesType = contract.contractType?.toLowerCase().includes(query);
      return matchesPartner || matchesType;
    }
    
    // If no search query, filter by selected year (or show all if "ALL")
    if (partnerYearFilter === 'ALL') {
      return true;
    }
    const expiryYear = new Date(contract.expiryDate).getFullYear();
    return expiryYear === parseInt(partnerYearFilter);
  });

  // Calculate stats (including both types)
  const allContracts = [...contracts, ...manualContracts];
  const totalContracts = allContracts.length;
  
  // For "Expiring in Year" stat, use appropriate year filter
  const displayYear = activeTab === 'partner' && partnerYearFilter === 'ALL' 
    ? 'All Years' 
    : (activeTab === 'partner' ? partnerYearFilter : selectedYear);
  const expiringInYear = filteredContracts.length + filteredManualContracts.length;
  
  // Expiring soon (next 6 months)
  const sixMonthsFromNow = new Date();
  sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
  const expiringSoon = allContracts.filter((contract) => {
    const expiryDate = new Date(contract.expiryDate);
    return expiryDate <= sixMonthsFromNow && expiryDate >= new Date();
  }).length;

  // Active contracts (not expired yet)
  const activeContracts = allContracts.filter((contract) => {
    return new Date(contract.expiryDate) >= new Date();
  }).length;

  return (
    <div style={{ padding: 20, color: '#fff' }}>
      {/* Header */}
      <div style={{ marginBottom: 30, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="header" style={{ margin: 0, marginBottom: 10 }}>
            Contract Tracking
          </h1>
          <p style={{ color: '#999', margin: 0, fontSize: 14 }}>
            Track contract expiration dates and manage your artist agreements
          </p>
          {lastFetched && (
            <p style={{ color: '#666', margin: '8px 0 0 0', fontSize: 12 }}>
              {isCached ? '📦 Cached data' : '✓ Fresh data'} · Last updated: {lastFetched.toLocaleTimeString()}
            </p>
          )}
        </div>
        <button
          onClick={() => fetchContracts(true)}
          disabled={loading}
          style={{
            padding: '10px 16px',
            background: loading ? '#555' : '#333',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.background = '#444';
          }}
          onMouseLeave={(e) => {
            if (!loading) e.currentTarget.style.background = '#333';
          }}
          title="Refresh data from Google Sheets"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              animation: loading ? 'spin 1s linear infinite' : 'none',
            }}
          >
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>
      
      {/* Add CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Info Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 20,
          marginBottom: 30,
        }}
      >
        {/* Total Contracts */}
        <div
          style={{
            background: '#1e1e1e',
            padding: 20,
            borderRadius: 10,
          }}
        >
          <div style={{ fontSize: 12, color: '#999', marginBottom: 8, fontWeight: 600 }}>
            TOTAL CONTRACTS
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: '#7eef4f' }}>
            {loading ? '—' : totalContracts}
          </div>
        </div>

        {/* Expiring This Year */}
        <div
          style={{
            background: '#1e1e1e',
            padding: 20,
            borderRadius: 10,
          }}
        >
          <div style={{ fontSize: 12, color: '#999', marginBottom: 8, fontWeight: 600 }}>
            {displayYear === 'All Years' ? 'IN CURRENT VIEW' : `EXPIRING IN ${displayYear}`}
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: '#f39c12' }}>
            {loading ? '—' : expiringInYear}
          </div>
        </div>

        {/* Expiring Soon (Next 6 Months) */}
        <div
          style={{
            background: '#1e1e1e',
            padding: 20,
            borderRadius: 10,
          }}
        >
          <div style={{ fontSize: 12, color: '#999', marginBottom: 8, fontWeight: 600 }}>
            EXPIRING SOON (6 MONTHS)
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: '#ea5a54' }}>
            {loading ? '—' : expiringSoon}
          </div>
        </div>

        {/* Active Contracts */}
        <div
          style={{
            background: '#1e1e1e',
            padding: 20,
            borderRadius: 10,
          }}
        >
          <div style={{ fontSize: 12, color: '#999', marginBottom: 8, fontWeight: 600 }}>
            ACTIVE CONTRACTS
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: '#28a745' }}>
            {loading ? '—' : activeContracts}
          </div>
        </div>
      </div>

      {/* TAB BAR */}
      <div style={{ marginTop: 40, borderBottom: '1px solid #333', display: 'flex', gap: 20 }}>
        {[
          { key: 'partner', label: 'Partner Contracts' },
          { key: 'artist', label: 'Artist Contracts' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: activeTab === t.key ? '#fff' : '#888',
              padding: '8px 0',
              fontSize: '1rem',
              borderBottom: activeTab === t.key ? '3px solid #28a745' : '3px solid transparent',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters Section */}
      <div style={{ marginTop: 30, marginBottom: 30 }}>
        {/* Search Bar */}
        <div style={{ marginBottom: 20, maxWidth: 500 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
            Search
          </label>
          <div style={{ position: 'relative' }}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#999"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
              }}
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder={
                activeTab === 'artist'
                  ? 'Search by artist or release name...'
                  : 'Search by partner name or contract type...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                background: '#1e1e1e',
                color: '#fff',
                border: 'none',
                padding: '10px 12px 10px 40px',
                borderRadius: 8,
                fontFamily: 'inherit',
                fontSize: 14,
                outline: 'none',
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#999',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Clear search"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Year Filter */}
        <div>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
            Filter by Expiration Year
          </label>
          {activeTab === 'artist' ? (
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              style={{
                appearance: 'none',
                background: '#1e1e1e',
                color: '#fff',
                border: 'none',
                padding: '10px 40px 10px 12px',
                borderRadius: 8,
                fontFamily: 'inherit',
                fontSize: 14,
                cursor: 'pointer',
                backgroundImage:
                  'url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNCA2TDggMTBMMTIgNiIgc3Ryb2tlPSIjOTk5IiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg==")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                backgroundSize: '16px',
              }}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          ) : (
            <select
              value={partnerYearFilter}
              onChange={(e) => setPartnerYearFilter(e.target.value)}
              style={{
                appearance: 'none',
                background: '#1e1e1e',
                color: '#fff',
                border: 'none',
                padding: '10px 40px 10px 12px',
                borderRadius: 8,
                fontFamily: 'inherit',
                fontSize: 14,
                cursor: 'pointer',
                backgroundImage:
                  'url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNCA2TDggMTBMMTIgNiIgc3Ryb2tlPSIjOTk5IiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg==")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                backgroundSize: '16px',
              }}
            >
              <option value="ALL">All Years</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          Loading contracts...
        </div>
      )}

      {/* Error State */}
      {error && (
        <div
          style={{
            padding: 20,
            background: '#2d1a1a',
            border: '1px solid #ea5a54',
            borderRadius: 8,
            color: '#ea5a54',
            marginBottom: 20,
          }}
        >
          Error: {error}
        </div>
      )}

      {/* Artist Contracts Tab */}
      {activeTab === 'artist' && (
        <>
          {!loading && !error && filteredContracts.length > 0 && (
            <div
            style={{
              background: '#1e1e1e',
              borderRadius: 10,
              overflow: 'hidden',
              marginBottom: 40,
            }}
          >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#2a2a2a' }}>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: 12,
                    color: '#999',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Artist Name
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: 12,
                    color: '#999',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Release Name
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: 12,
                    color: '#999',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Expiry Date
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: 12,
                    color: '#999',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredContracts.map((contract, index) => {
                const expiryDate = new Date(contract.expiryDate);
                const now = new Date();
                const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
                
                let statusColor = '#28a745'; // green - active
                let statusText = 'Active';
                
                if (daysUntilExpiry < 0) {
                  statusColor = '#666'; // gray - expired
                  statusText = 'Expired';
                } else if (daysUntilExpiry <= 180) {
                  statusColor = '#ea5a54'; // red - expiring soon
                  statusText = 'Expiring Soon';
                } else if (daysUntilExpiry <= 365) {
                  statusColor = '#f39c12'; // orange - expiring this year
                  statusText = 'Expiring This Year';
                }

                return (
                  <tr
                    key={index}
                    style={{
                      borderTop: '1px solid #2a2a2a',
                    }}
                  >
                    <td style={{ padding: '12px 16px', color: '#fff' }}>
                      {contract.artistName || '—'}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#fff' }}>
                      {contract.releaseName || '—'}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#ddd' }}>
                      {contract.expiryDateFormatted}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight: 600,
                          background: `${statusColor}22`,
                          color: statusColor,
                        }}
                      >
                        {statusText}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          )}

          {/* Empty State for Artist Contracts */}
          {!loading && !error && filteredContracts.length === 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '80px 20px',
                background: '#1e1e1e',
                borderRadius: 12,
                border: '2px dashed #333',
              }}
            >
              <svg
                width="80"
                height="80"
                viewBox="0 0 24 24"
                fill="none"
                style={{ marginBottom: 20, opacity: 0.3 }}
              >
                <path
                  d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <polyline
                  points="14 2 14 8 20 8"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <line
                  x1="16"
                  y1="13"
                  x2="8"
                  y2="13"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <line
                  x1="16"
                  y1="17"
                  x2="8"
                  y2="17"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <polyline
                  points="10 9 9 9 8 9"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <h2 style={{ margin: 0, marginBottom: 10, fontSize: 20, color: '#ddd' }}>
                No Contracts Found
              </h2>
              <p style={{ margin: 0, color: '#999', fontSize: 14, maxWidth: 400, textAlign: 'center' }}>
                {contracts.length === 0
                  ? 'No contracts found in the Google Sheet. Make sure the "Release Catalogue Details" sheet has data with expiry dates in column AC.'
                  : searchQuery
                  ? `No contracts found matching "${searchQuery}". Try a different search term.`
                  : `No contracts expiring in ${selectedYear}. Try selecting a different year.`}
              </p>
            </div>
          )}
        </>
      )}

      {/* Partner Contracts Tab */}
      {activeTab === 'partner' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 15 }}>
            <button
              onClick={handleAddManualContract}
              style={{
                padding: '10px 16px',
                background: '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
              Add Contract
            </button>
          </div>

        {filteredManualContracts.length > 0 ? (
          <div
            style={{
              background: '#1e1e1e',
              borderRadius: 10,
              overflow: 'hidden',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#2a2a2a' }}>
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: 12,
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Partner Name
                  </th>
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: 12,
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Contract Type
                  </th>
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: 12,
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Expiry Date
                  </th>
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: 12,
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Notice Deadline
                  </th>
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: 12,
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Days Until
                  </th>
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: 12,
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: 12,
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredManualContracts.map((contract) => {
                  const expiryDate = new Date(contract.expiryDate);
                  const now = new Date();
                  
                  // Calculate notice deadline (subtract notice months from expiry date)
                  const noticeDeadline = new Date(expiryDate);
                  noticeDeadline.setMonth(noticeDeadline.getMonth() - (contract.noticeMonths || 0));
                  
                  const daysUntilNotice = Math.ceil((noticeDeadline - now) / (1000 * 60 * 60 * 24));
                  const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
                  
                  let statusColor = '#28a745'; // green - active
                  let statusText = 'Active';
                  
                  if (daysUntilExpiry < 0) {
                    statusColor = '#666'; // gray - expired
                    statusText = 'Expired';
                  } else if (daysUntilExpiry <= 180) {
                    statusColor = '#ea5a54'; // red - expiring soon
                    statusText = 'Expiring Soon';
                  } else if (daysUntilExpiry <= 365) {
                    statusColor = '#f39c12'; // orange - expiring this year
                    statusText = 'Expiring This Year';
                  }

                  return (
                    <tr
                      key={contract._id}
                      style={{
                        borderTop: '1px solid #2a2a2a',
                      }}
                    >
                      <td style={{ padding: '12px 16px', color: '#fff' }}>
                        {contract.partnerName}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#fff' }}>
                        {contract.contractType || '—'}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#ddd' }}>
                        {expiryDate.toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#ddd' }}>
                        {contract.noticeMonths > 0 ? (
                          noticeDeadline.toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#ddd' }}>
                        {contract.noticeMonths > 0 ? (
                          <span
                            style={{
                              color: daysUntilNotice < 0 
                                ? '#ea5a54' 
                                : daysUntilNotice <= 30 
                                ? '#ea5a54' 
                                : daysUntilNotice <= 60 
                                ? '#f39c12' 
                                : '#ddd',
                              fontWeight: daysUntilNotice <= 60 ? 600 : 400,
                            }}
                          >
                            {daysUntilNotice < 0 ? `${Math.abs(daysUntilNotice)} days overdue` : `${daysUntilNotice} days`}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 8px',
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 600,
                            background: `${statusColor}22`,
                            color: statusColor,
                          }}
                        >
                          {statusText}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => handleEditManualContract(contract)}
                            style={{
                              padding: '6px 12px',
                              background: '#555',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteManualContract(contract._id)}
                            style={{
                              padding: '6px 12px',
                              background: '#ea5a54',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            style={{
              padding: 40,
              background: '#1e1e1e',
              borderRadius: 10,
              textAlign: 'center',
              color: '#999',
            }}
          >
            {searchQuery ? (
              <p style={{ margin: 0 }}>No partner contracts found matching "{searchQuery}".</p>
            ) : (
              <p style={{ margin: 0 }}>
                No partner contracts {partnerYearFilter === 'ALL' ? 'found' : `expiring in ${partnerYearFilter}`}. Click "Add Contract" to create one.
              </p>
            )}
          </div>
        )}
        </div>
      )}

      {/* Manual Contract Modal */}
      {showManualContractModal && (
        <div
          className="modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => {
            setShowManualContractModal(false);
            setEditingContract(null);
          }}
        >
          <div
            className="modal-content"
            style={{
              background: '#1e1e1e',
              borderRadius: 12,
              padding: 40,
              maxWidth: 550,
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              color: '#fff',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, marginBottom: 30, fontSize: 24 }}>
              {editingContract ? 'Edit Contract' : 'Add Partner Contract'}
            </h2>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#ddd' }}>
                Partner Name *
              </label>
              <input
                type="text"
                value={formData.partnerName}
                onChange={(e) => setFormData({ ...formData, partnerName: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  background: '#2a2a2a',
                  border: 'none',
                  borderRadius: 8,
                  color: '#fff',
                  fontSize: 14,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
                placeholder="e.g. Sony Music, Universal, etc."
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#ddd' }}>
                Contract Type
              </label>
              <input
                type="text"
                value={formData.contractType}
                onChange={(e) => setFormData({ ...formData, contractType: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  background: '#2a2a2a',
                  border: 'none',
                  borderRadius: 8,
                  color: '#fff',
                  fontSize: 14,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
                placeholder="e.g. Distribution, Publishing, etc."
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#ddd' }}>
                Expiry Date *
              </label>
              <input
                type="date"
                value={formData.expiryDate}
                onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  background: '#2a2a2a',
                  border: 'none',
                  borderRadius: 8,
                  color: '#fff',
                  fontSize: 14,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#ddd' }}>
                Notice Deadline (months before expiry)
              </label>
              <input
                type="number"
                min="0"
                value={formData.noticeMonths}
                onChange={(e) => setFormData({ ...formData, noticeMonths: parseInt(e.target.value) || 0 })}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  background: '#2a2a2a',
                  border: 'none',
                  borderRadius: 8,
                  color: '#fff',
                  fontSize: 14,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
                placeholder="e.g. 2 for 2 months before expiry"
              />
            </div>

            <div style={{ marginBottom: 32 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#ddd' }}>
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  background: '#2a2a2a',
                  border: 'none',
                  borderRadius: 8,
                  color: '#fff',
                  fontSize: 14,
                  fontFamily: 'inherit',
                  minHeight: 100,
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
                placeholder="Add any additional notes..."
              />
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 32, paddingTop: 24, borderTop: '1px solid #333' }}>
              <button
                onClick={() => {
                  setShowManualContractModal(false);
                  setEditingContract(null);
                }}
                style={{
                  padding: '12px 24px',
                  background: '#555',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#666'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#555'}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveManualContract}
                style={{
                  padding: '12px 24px',
                  background: '#28a745',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#32CD32'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#28a745'}
              >
                {editingContract ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

